import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Plus, Search, FileText, Download, Trash2, Edit, Calendar, DollarSign, Tag, Link as LinkIcon, Eye, Upload, FileSpreadsheet } from 'lucide-react';

const Egresos = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [gastos, setGastos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Date filter state
  const [filterType, setFilterType] = useState("mes_actual");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  
  const [filtros, setFiltros] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    categoria_id: '',
    tipo_gasto: '',
    ligado_a: '',
    estatus: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [gastoActual, setGastoActual] = useState(null);
  
  // CSV upload state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  
  // Bulk selection state
  const [selectedGastos, setSelectedGastos] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, [filterType, dateRange]);

  // Download CSV template
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos-csv/plantilla`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'plantilla_gastos.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error al descargar la plantilla');
    }
  };

  // Upload CSV file
  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setCsvUploading(true);
    setCsvResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/gastos-csv/carga-masiva`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setCsvResult(result);
        if (result.exitosos > 0) {
          fetchData(); // Refresh list
        }
      } else {
        setCsvResult({
          total_filas: 0,
          exitosos: 0,
          errores: 1,
          detalle_errores: [{ fila: 0, error: result.detail || 'Error desconocido' }]
        });
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setCsvResult({
        total_filas: 0,
        exitosos: 0,
        errores: 1,
        detalle_errores: [{ fila: 0, error: 'Error de conexión' }]
      });
    } finally {
      setCsvUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      // Calculate date range based on filter
      let startDate = "";
      let endDate = "";
      
      if (filterType === "mes_actual") {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
      } else if (filterType === "ultimos_30") {
        const today = new Date();
        const past = new Date(today);
        past.setDate(past.getDate() - 30);
        startDate = past.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (filterType === "ultimos_60") {
        const today = new Date();
        const past = new Date(today);
        past.setDate(past.getDate() - 60);
        startDate = past.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (filterType === "personalizado" && dateRange.from && dateRange.to) {
        startDate = dateRange.from.toISOString().split('T')[0];
        endDate = dateRange.to.toISOString().split('T')[0];
      }

      // Build query params
      let queryParams = `limit=500`;
      if (startDate) queryParams += `&fecha_inicio=${startDate}`;
      if (endDate) queryParams += `&fecha_fin=${endDate}`;

      // Fetch gastos
      const gastosRes = await fetch(`${API_URL}/api/gastos?${queryParams}`, { headers });
      if (gastosRes.ok) {
        const data = await gastosRes.json();
        setGastos(data);
      }

      // Fetch categorias
      const categoriasRes = await fetch(`${API_URL}/api/gastos/categorias`, { headers });
      if (categoriasRes.ok) {
        const data = await categoriasRes.json();
        setCategorias(data);
      }

      // Fetch contratos
      const contratosRes = await fetch(`${API_URL}/api/contracts?limit=500`, { headers });
      if (contratosRes.ok) {
        const data = await contratosRes.json();
        setContratos(data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (gastoId) => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar este gasto?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/${gastoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Gasto cancelado exitosamente');
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al cancelar el gasto');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al cancelar el gasto');
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select only active gastos
      const activeGastoIds = gastosFiltrados
        .filter(g => g.estatus === 'activo')
        .map(g => g.id);
      setSelectedGastos(activeGastoIds);
    } else {
      setSelectedGastos([]);
    }
  };

  const handleSelectGasto = (gastoId) => {
    setSelectedGastos(prev => {
      if (prev.includes(gastoId)) {
        return prev.filter(id => id !== gastoId);
      } else {
        return [...prev, gastoId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedGastos.length === 0) return;
    
    setBulkDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/eliminar-masivo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gasto_ids: selectedGastos })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`${result.eliminados} gasto(s) eliminado(s) exitosamente`);
        setSelectedGastos([]);
        setShowDeleteConfirm(false);
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al eliminar los gastos');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar los gastos');
    } finally {
      setBulkDeleting(false);
    }
  };

  const gastosFiltrados = gastos.filter(gasto => {
    if (filtros.categoria_id && gasto.categoria_id !== filtros.categoria_id) return false;
    if (filtros.tipo_gasto && gasto.tipo_gasto !== filtros.tipo_gasto) return false;
    if (filtros.ligado_a && gasto.ligado_a !== filtros.ligado_a) return false;
    if (filtros.estatus && gasto.estatus !== filtros.estatus) return false;
    return true;
  });

  // Check if all active gastos are selected
  const activeGastos = gastosFiltrados.filter(g => g.estatus === 'activo');
  const allSelected = activeGastos.length > 0 && selectedGastos.length === activeGastos.length;
  const someSelected = selectedGastos.length > 0 && selectedGastos.length < activeGastos.length;

  const totalGastos = gastosFiltrados.reduce((sum, g) => sum + g.monto, 0);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Egresos</h1>
            <p className="text-gray-600 mt-1">Gestiona todos los gastos de la empresa</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* CSV Buttons */}
            <button
              onClick={handleDownloadTemplate}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2"
              data-testid="btn-download-csv-template"
              title="Descargar plantilla CSV"
            >
              <Download className="h-5 w-5" />
              <span className="hidden sm:inline">Plantilla CSV</span>
            </button>
            <button
              onClick={() => setShowCsvModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
              data-testid="btn-upload-csv"
              title="Cargar gastos desde CSV"
            >
              <Upload className="h-5 w-5" />
              <span className="hidden sm:inline">Cargar CSV</span>
            </button>
            <button
              onClick={() => navigate('/egresos/recurrentes')}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
            >
              <Calendar className="h-5 w-5" />
              <span className="hidden sm:inline">Gastos Recurrentes</span>
            </button>
            <button
              onClick={() => navigate('/egresos/nuevo')}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Nuevo Gasto
            </button>
          </div>
        </div>

        {/* Bulk Selection Bar */}
        {selectedGastos.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={handleSelectAll}
                className="h-5 w-5 text-red-600 rounded"
              />
              <span className="text-red-800 font-medium">
                {selectedGastos.length} gasto(s) seleccionado(s)
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedGastos([])}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                data-testid="btn-bulk-delete"
              >
                <Trash2 className="h-4 w-4" />
                Eliminar Seleccionados
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Gastos</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${totalGastos.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <DollarSign className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Gastos Registrados</p>
                <p className="text-2xl font-bold text-gray-900">{gastosFiltrados.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Categorías Activas</p>
                <p className="text-2xl font-bold text-gray-900">{categorias.length}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Tag className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Date Filter */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Período:</label>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType("mes_actual")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filterType === "mes_actual"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Este mes
              </button>
              <button
                onClick={() => setFilterType("ultimos_30")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filterType === "ultimos_30"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Últimos 30 días
              </button>
              <button
                onClick={() => setFilterType("ultimos_60")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filterType === "ultimos_60"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Últimos 60 días
              </button>
              <button
                onClick={() => setFilterType("personalizado")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filterType === "personalizado"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Personalizado
              </button>
            </div>
          </div>

          {filterType === "personalizado" && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                <input
                  type="date"
                  value={dateRange.from ? dateRange.from.toISOString().split('T')[0] : ''}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value ? new Date(e.target.value) : null })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                <input
                  type="date"
                  value={dateRange.to ? dateRange.to.toISOString().split('T')[0] : ''}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value ? new Date(e.target.value) : null })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
          )}
        </div>

        {/* Additional Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
              <select
                value={filtros.categoria_id}
                onChange={(e) => setFiltros({ ...filtros, categoria_id: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todas</option>
                {categorias.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={filtros.tipo_gasto}
                onChange={(e) => setFiltros({ ...filtros, tipo_gasto: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todos</option>
                <option value="unico">Único</option>
                <option value="recurrente">Recurrente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ligado a</label>
              <select
                value={filtros.ligado_a}
                onChange={(e) => setFiltros({ ...filtros, ligado_a: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todos</option>
                <option value="contrato">Contrato</option>
                <option value="propietario">Propietario</option>
                <option value="ninguno">Ninguno</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estatus</label>
              <select
                value={filtros.estatus}
                onChange={(e) => setFiltros({ ...filtros, estatus: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todos</option>
                <option value="activo">Activo</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => setFiltros({ categoria_id: '', tipo_gasto: '', ligado_a: '', estatus: '' })}
              className="text-sm text-indigo-600 hover:text-indigo-800"
            >
              Limpiar filtros
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando gastos...</p>
            </div>
          ) : gastosFiltrados.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron gastos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="py-3 px-4 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={el => { if (el) el.indeterminate = someSelected; }}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Fecha</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Concepto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Monto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Categoría</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Ligado a</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Archivos</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Estatus</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {gastosFiltrados.map((gasto, index) => (
                    <tr 
                      key={gasto.id} 
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${selectedGastos.includes(gasto.id) ? 'bg-red-50' : ''}`}
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedGastos.includes(gasto.id)}
                          onChange={() => handleSelectGasto(gasto.id)}
                          disabled={gasto.estatus !== 'activo'}
                          className="h-4 w-4 text-indigo-600 rounded border-gray-300 disabled:opacity-50"
                          data-testid={`checkbox-gasto-${gasto.id}`}
                        />
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {new Date(gasto.fecha_gasto).toLocaleDateString('es-MX')}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        <div className="font-medium">{gasto.concepto}</div>
                        {gasto.descripcion && (
                          <div className="text-xs text-gray-500 mt-1">{gasto.descripcion.substring(0, 50)}...</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-gray-900">
                        ${gasto.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {gasto.categoria_nombre || 'Sin categoría'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {gasto.tipo_gasto === 'recurrente' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Recurrente
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Único
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {gasto.ligado_a === 'contrato' && (
                          <div>
                            {gasto.inmueble_nombre ? (
                              <span className="text-xs font-medium">{gasto.inmueble_nombre}</span>
                            ) : gasto.contrato_numero ? (
                              <span className="text-xs">{gasto.contrato_numero}</span>
                            ) : (
                              <span className="text-xs text-gray-400">Sin info</span>
                            )}
                          </div>
                        )}
                        {gasto.ligado_a === 'propietario' && gasto.propietario_nombre && (
                          <span className="text-xs">{gasto.propietario_nombre}</span>
                        )}
                        {gasto.ligado_a === 'ninguno' && (
                          <span className="text-xs text-gray-500">General</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {gasto.archivos_count > 0 && (
                          <span className="inline-flex items-center text-xs text-indigo-600">
                            <FileText className="h-4 w-4 mr-1" />
                            {gasto.archivos_count}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {gasto.estatus === 'activo' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Cancelado
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/egresos/${gasto.id}`)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Ver detalles"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {gasto.estatus === 'activo' && (
                            <>
                              <button
                                onClick={() => navigate(`/egresos/${gasto.id}/editar`)}
                                className="text-indigo-600 hover:text-indigo-800"
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(gasto.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Cancelar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CSV Upload Modal */}
        {showCsvModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <FileSpreadsheet className="h-6 w-6 text-blue-600" />
                  Carga Masiva de Gastos
                </h2>
                <button 
                  onClick={() => { setShowCsvModal(false); setCsvResult(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {!csvResult ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-medium text-blue-800 mb-2">Instrucciones:</h3>
                    <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                      <li>Descarga la plantilla CSV haciendo clic en "Plantilla CSV"</li>
                      <li>Llena la plantilla con los datos de los gastos (únicos o recurrentes)</li>
                      <li>Sube el archivo CSV aquí</li>
                    </ol>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <h3 className="font-medium text-gray-800 mb-2">Columnas del CSV:</h3>
                    
                    <p className="text-xs font-semibold text-gray-700 mt-2 mb-1">Campos comunes (obligatorios):</p>
                    <ul className="text-xs text-gray-600 space-y-0.5 ml-2">
                      <li><strong>monto:</strong> Cantidad numérica (ej: 1500.00)</li>
                      <li><strong>concepto:</strong> Descripción breve del gasto</li>
                      <li><strong>descripcion:</strong> Detalle adicional (opcional)</li>
                      <li><strong>categoria_nombre:</strong> Nombre exacto de la categoría</li>
                      <li><strong>ligado_a:</strong> "contrato" o "propietario"</li>
                      <li><strong>contrato_numero:</strong> Número del contrato (si ligado_a=contrato)</li>
                      <li><strong>propietario_email:</strong> Email del propietario (si ligado_a=propietario)</li>
                      <li><strong>tipo_gasto:</strong> "unico" o "recurrente" (default: unico)</li>
                    </ul>
                    
                    <p className="text-xs font-semibold text-green-700 mt-3 mb-1">Para gastos ÚNICOS:</p>
                    <ul className="text-xs text-gray-600 space-y-0.5 ml-2">
                      <li><strong>fecha_gasto:</strong> Formato YYYY-MM-DD</li>
                    </ul>
                    
                    <p className="text-xs font-semibold text-purple-700 mt-3 mb-1">Para gastos RECURRENTES:</p>
                    <ul className="text-xs text-gray-600 space-y-0.5 ml-2">
                      <li><strong>frecuencia:</strong> mensual, bimestral, trimestral, anual</li>
                      <li><strong>dia_generacion:</strong> Día del mes (1-31)</li>
                      <li><strong>fecha_inicio:</strong> Formato YYYY-MM-DD</li>
                      <li><strong>fecha_fin:</strong> Formato YYYY-MM-DD (opcional)</li>
                    </ul>
                  </div>

                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition">
                    <Upload className="h-12 w-12 text-gray-400 mb-2" />
                    <label className="cursor-pointer">
                      <span className="text-blue-600 hover:text-blue-800 font-medium">
                        {csvUploading ? 'Procesando...' : 'Seleccionar archivo CSV'}
                      </span>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleCsvUpload}
                        disabled={csvUploading}
                        className="hidden"
                        data-testid="csv-file-input"
                      />
                    </label>
                    <p className="text-sm text-gray-500 mt-1">Solo archivos .csv</p>
                  </div>

                  {csvUploading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Procesando archivo...</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Result Summary */}
                  <div className={`p-4 rounded-lg ${csvResult.errores === 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <h3 className={`font-bold ${csvResult.errores === 0 ? 'text-green-800' : 'text-yellow-800'}`}>
                      Resultado de la Carga
                    </h3>
                    <div className="mt-2 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{csvResult.total_filas}</p>
                        <p className="text-sm text-gray-600">Total Filas</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{csvResult.exitosos}</p>
                        <p className="text-sm text-gray-600">Exitosos</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600">{csvResult.errores}</p>
                        <p className="text-sm text-gray-600">Errores</p>
                      </div>
                    </div>
                  </div>

                  {/* Error Details */}
                  {csvResult.detalle_errores.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <h4 className="font-medium text-red-800 mb-2">Detalle de Errores:</h4>
                      <ul className="text-sm text-red-700 space-y-2">
                        {csvResult.detalle_errores.map((err, idx) => (
                          <li key={idx} className="border-b border-red-200 pb-2">
                            <strong>Fila {err.fila}:</strong> {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setCsvResult(null)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Cargar Otro Archivo
                    </button>
                    <button
                      onClick={() => { setShowCsvModal(false); setCsvResult(null); }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Confirmar Eliminación
                </h2>
              </div>

              <p className="text-gray-600 mb-4">
                ¿Estás seguro de que deseas eliminar <strong>{selectedGastos.length}</strong> gasto(s)? 
                Esta acción no se puede deshacer.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> Los gastos serán marcados como "cancelados" y no aparecerán en los reportes.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={bulkDeleting}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2 disabled:opacity-50"
                  data-testid="confirm-bulk-delete"
                >
                  {bulkDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Eliminar {selectedGastos.length} gasto(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Egresos;
